import { api } from './api';
import { Movimento, MovimentoFilters, Risorsa } from '../types';

/**
 * Request interface for creating/updating Movimento
 * Matches backend MovimentoRequest DTO
 */
export interface MovimentoRequest {
  descrizione: string;
  tipo: 'ENTRATA' | 'USCITA';
  importo: number;
  data: string;
  categoriaId: number;
  note?: string;
}

/**
 * Legacy request format (from forms)
 */
export interface MovimentoFormData {
  tipo: 'ENTRATA' | 'USCITA';
  importo: number;
  dataMovimento?: string;
  data?: string;
  descrizione?: string;
  note?: string;
  isPagato?: boolean;
  categoriaId: number;
  sottocategoriaId?: number;
  clienteId?: number;
  risorsaId?: number;
  articoloId?: number;
  tipoPagamento?: string;
  quantita?: number;
  numeroOrdine?: string;
  /** Riga d'ordine senza articolo: descrizione della voce (spedizione, imballo, ...) */
  voce?: string;
  comune?: string;
  provincia?: string;
  // Rate fields
  numeroRate?: number;
  frequenzaRate?: string;
  dataScadenza?: string;
  dividiImporto?: boolean;
}

/**
 * Request interface for creating a Giroconto (transfer between accounts)
 */
export interface GirocontoRequest {
  importo: number;
  data: string;
  risorsaId: number;           // Source account
  risorsaDestinazioneId: number; // Destination account
  note?: string;
  numeroOrdine?: string;
}

/**
 * Backend response format
 */
interface MovimentoBackend {
  id: number;
  tipo: 'ENTRATA' | 'USCITA';
  descrizione?: string;
  importo: number;
  data?: string;
  dataMovimento?: string;
  note?: string;
  categoriaId: number;
  categoriaNome?: string;
  risorsaId?: number;
  risorsaNome?: string;
  risorsaTipo?: string;
  clienteId?: number;
  clienteNome?: string;
  clienteCodice?: string;
  articoloId?: number;
  articoloNome?: string;
  tipoPagamento?: string;
  quantita?: number;
  isPagato?: boolean;
  numeroOrdine?: string;
  voce?: string;
  comune?: string;
  provincia?: string;
  createdAt?: string;
  updatedAt?: string;
  // Rate fields
  numeroRata?: number;
  totaleRate?: number;
  gruppoRateId?: string;
  dataScadenza?: string;
  frequenzaRate?: string;
}

/**
 * Transform backend response to frontend Movimento type
 */
const transformToMovimento = (response: MovimentoBackend): Movimento => {
  return {
    id: response.id,
    tipo: response.tipo.toLowerCase() as 'entrata' | 'uscita',
    importo: response.importo,
    data: response.data || response.dataMovimento || '',
    descrizione: response.descrizione || response.note || '',
    categoria_id: response.categoriaId,
    categoria: response.categoriaNome ? { id: response.categoriaId, nome: response.categoriaNome, tipo: 'entrambi', attiva: true, utente_id: 0 } : undefined,
    risorsa_id: response.risorsaId,
    risorsa: response.risorsaNome ? { id: response.risorsaId!, nome: response.risorsaNome, tipo: (response.risorsaTipo as Risorsa['tipo']) || 'altro', saldo_iniziale: 0, saldo_corrente: 0, attiva: true, utente_id: 0 } : undefined,
    rubrica_id: response.clienteId,
    contatto: response.clienteNome ? { id: response.clienteId!, nome: response.clienteNome, codiceProgressivo: response.clienteCodice, utente_id: 0 } : undefined,
    articolo_id: response.articoloId,
    articolo: response.articoloNome ? { id: response.articoloId!, nome: response.articoloNome, codice: '', prezzoAcquisto: 0, attivo: true, utente_id: 0, quantita: 0, scortaMinima: 0, stato: 'disponibile' } : undefined,
    tipoPagamento: response.tipoPagamento,
    quantita: response.quantita,
    note: response.note,
    isPagato: response.isPagato,
    numeroOrdine: response.numeroOrdine,
    voce: response.voce || undefined,
    comune: response.comune,
    provincia: response.provincia,
    utente_id: 0,
    data_creazione: response.createdAt || response.data || '',
    data_modifica: response.updatedAt,
    // Rate fields
    numeroRata: response.numeroRata,
    totaleRate: response.totaleRate,
    gruppoRateId: response.gruppoRateId,
    dataScadenza: response.dataScadenza,
    frequenzaRate: response.frequenzaRate,
  };
};

/**
 * Transform form data to backend request format
 */
const transformToBackendRequest = (formData: MovimentoFormData): Record<string, unknown> => {
  return {
    descrizione: formData.descrizione || formData.note || `Movimento ${formData.tipo}`,
    tipo: formData.tipo,
    importo: formData.importo,
    data: formData.data || formData.dataMovimento || new Date().toISOString().split('T')[0],
    categoriaId: formData.categoriaId,
    note: formData.note,
    clienteId: formData.clienteId,
    risorsaId: formData.risorsaId,
    articoloId: formData.articoloId,
    tipoPagamento: formData.tipoPagamento,
    quantita: formData.quantita,
    isPagato: formData.isPagato,
    numeroOrdine: formData.numeroOrdine,
    voce: formData.voce,
    comune: formData.comune,
    provincia: formData.provincia,
    // Rate fields
    numeroRate: formData.numeroRate,
    frequenzaRate: formData.frequenzaRate,
    dataScadenza: formData.dataScadenza,
    dividiImporto: formData.dividiImporto,
  };
};

/**
 * Get all movimenti with optional filters
 */
const getMovimenti = async (filters?: MovimentoFilters): Promise<Movimento[]> => {
  const params: Record<string, string | number | boolean> = {};

  if (filters?.data_inizio) params.from = filters.data_inizio;
  if (filters?.data_fine) params.to = filters.data_fine;
  if (filters?.tipo && filters.tipo !== 'tutti') params.tipo = filters.tipo.toUpperCase();
  if (filters?.categoria_id) params.categoriaId = String(filters.categoria_id);
  if (filters?.articolo_id) params.articoloId = String(filters.articolo_id);
  if (filters?.cliente_id) params.clienteId = String(filters.cliente_id);
  if (filters?.risorsa_id) params.risorsaId = String(filters.risorsa_id);
  if (filters?.ordine) params.numeroOrdine = filters.ordine;
  if (filters?.tipo_pagamento) params.tipoPagamento = filters.tipo_pagamento;
  if (filters?.provincia) params.provincia = filters.provincia;
  if (filters?.citta) params.citta = filters.citta;
  
  // Handle pagato/non_pagato. Backend might expect 'isPagato' true/false/null
  if (filters?.pagato && !filters.non_pagato) {
    params.isPagato = true;
  } else if (!filters?.pagato && filters?.non_pagato) {
    params.isPagato = false;
  }
  // If both are true or both are false, we don't add the filter (show all)

  const response = await api.get<MovimentoBackend[]>('/movimenti', { 
    params: Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null)) 
  });
  return response.data.map(transformToMovimento);
};

/**
 * Get a single movimento by ID
 */
const getMovimentoById = async (id: number): Promise<Movimento> => {
  const response = await api.get<MovimentoBackend>(`/movimenti/${id}`);
  return transformToMovimento(response.data);
};

/**
 * Create a new movimento (or multiple if rate are specified)
 * Returns the first movimento for backwards compatibility
 */
const createMovimento = async (movimento: MovimentoFormData | MovimentoRequest): Promise<Movimento> => {
  const backendRequest = 'dataMovimento' in movimento || !('descrizione' in movimento)
    ? transformToBackendRequest(movimento as MovimentoFormData)
    : movimento as MovimentoRequest;

  const response = await api.post<MovimentoBackend[]>('/movimenti', backendRequest);
  // Backend now returns an array, return first element for backwards compatibility
  const data = Array.isArray(response.data) ? response.data[0] : response.data;
  return transformToMovimento(data);
};

/**
 * Create multiple movimenti (rate)
 * Returns all created movements
 */
const createMovimentiRate = async (movimento: MovimentoFormData): Promise<Movimento[]> => {
  const backendRequest = transformToBackendRequest(movimento);
  const response = await api.post<MovimentoBackend[]>('/movimenti', backendRequest);
  const data = Array.isArray(response.data) ? response.data : [response.data];
  return data.map(transformToMovimento);
};

/**
 * Update an existing movimento
 */
const updateMovimento = async (
  id: number,
  movimento: Partial<MovimentoFormData | MovimentoRequest>
): Promise<Movimento> => {
  const backendRequest = 'dataMovimento' in movimento || !('descrizione' in movimento)
    ? transformToBackendRequest(movimento as MovimentoFormData)
    : movimento as MovimentoRequest;

  const response = await api.put<MovimentoBackend>(`/movimenti/${id}`, backendRequest);
  return transformToMovimento(response.data);
};

/**
 * Delete a movimento
 */
const deleteMovimento = async (id: number): Promise<void> => {
  await api.delete(`/movimenti/${id}`);
};

/**
 * Create a giroconto (transfer between accounts)
 * Returns both created movements (USCITA from source, ENTRATA to destination)
 */
const createGiroconto = async (giroconto: GirocontoRequest): Promise<Movimento[]> => {
  const backendRequest = {
    importo: giroconto.importo,
    data: giroconto.data,
    risorsaId: giroconto.risorsaId,
    risorsaDestinazioneId: giroconto.risorsaDestinazioneId,
    note: giroconto.note,
    numeroOrdine: giroconto.numeroOrdine,
    // tipoPagamento intentionally omitted for giroconti
  };

  const response = await api.post<MovimentoBackend[]>('/movimenti/giroconto', backendRequest);
  const data = Array.isArray(response.data) ? response.data : [response.data];
  return data.map(transformToMovimento);
};

/**
 * Export movimenti to CSV
 */
const exportMovimenti = async (filters?: MovimentoFilters): Promise<void> => {
  const params: Record<string, string> = {};

  if (filters?.data_inizio) {
    params.from = filters.data_inizio;
  }

  if (filters?.data_fine) {
    params.to = filters.data_fine;
  }

  const response = await api.get('/movimenti/export', {
    params,
    responseType: 'blob',
  });

  // Create blob and download
  const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;

  // Get filename from Content-Disposition header or use default
  const contentDisposition = response.headers['content-disposition'];
  let filename = 'movimenti.csv';
  if (contentDisposition) {
    const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (match && match[1]) {
      filename = match[1].replace(/['"]/g, '');
    }
  }

  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Numero ordine proposto per un nuovo movimento (ORD-AAAA-NNNN, progressivo).
 * È solo una proposta: il campo resta modificabile e il numero non viene riservato.
 */
const getProssimoNumeroOrdine = async (): Promise<string> => {
  const response = await api.get<{ numeroOrdine: string }>('/movimenti/ordini/prossimo-numero');
  return response.data.numeroOrdine;
};

/**
 * Voce libera già usata negli ordini, con l'ultimo prezzo e reparto usati
 */
export interface VoceRicorrente {
  voce: string;
  prezzoUnitario: number;
  categoriaId?: number;
  categoriaNome?: string;
  utilizzi: number;
}

const getVociRicorrenti = async (tipo?: 'ENTRATA' | 'USCITA'): Promise<VoceRicorrente[]> => {
  const response = await api.get<VoceRicorrente[]>('/movimenti/voci-ricorrenti', {
    params: tipo ? { tipo } : undefined,
  });
  return response.data;
};

const movimentiService = {
  getProssimoNumeroOrdine,
  getVociRicorrenti,
  getMovimenti,
  getMovimentoById,
  createMovimento,
  createMovimentiRate,
  createGiroconto,
  updateMovimento,
  deleteMovimento,
  exportMovimenti,
};

export default movimentiService;
